import { useState, useEffect, useMemo } from 'react';
import debounce from 'lodash/debounce';
import {
  FilterConfig,
  FilterPlacement,
  FilterRenderMode,
  SelectFilterConfig,
  DateFilterConfig,
  DateRangeFilterConfig,
  SearchFilterConfig,
} from './types';
import { ChicletFilter } from './ChicletFilter';
import { SelectFilter } from './MultiSelectFilter';
import { DateFilter } from './DateFilter';
import { FilterMenu } from './FilterMenu';
import { MdFilterList } from 'react-icons/md';
import { SearchFilter } from './SearchFilter';

export type { FilterConfig } from './types';
export { FilterPlacement, FilterRenderMode } from './types';

interface PageFiltersProps {
  filters: FilterConfig[];
  maxChicletFilters?: number;
}

export const PageFilters = ({ filters, maxChicletFilters }: PageFiltersProps) => {
  const [selections, setSelections] = useState<Record<string, Set<string>>>(() =>
    Object.fromEntries(
      filters
        .filter((f): f is SelectFilterConfig => f.type === 'select')
        .map((f) => [f.id, new Set(f.options.filter((o) => o.selected).map((o) => o.value))])
    )
  );

  const [dateSelections, setDateSelections] = useState<Record<string, Date | null>>(() =>
    Object.fromEntries(
      filters
        .filter((f): f is DateFilterConfig => f.type === 'date')
        .map((f) => [f.id, f.selected ?? null])
    )
  );

  const [dateRangeSelections, setDateRangeSelections] = useState<
    Record<string, [Date | null, Date | null]>
  >(() =>
    Object.fromEntries(
      filters
        .filter((f): f is DateRangeFilterConfig => f.type === 'dateRange')
        .map((f) => [f.id, [f.startDate ?? null, f.endDate ?? null]])
    )
  );

  const [searchTerms, setSearchTerms] = useState<Record<string, string | null>>(() =>
    Object.fromEntries(
      filters
        .filter((f): f is SearchFilterConfig => f.type === 'search')
        .map((f) => [f.id, f.searchTerm ?? null])
    )
  );

  const [windowWidth, setWindowWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth < 600;

  const [placements, setPlacements] = useState<Record<string, FilterPlacement>>(() =>
    Object.fromEntries(filters.map((f) => [f.id, f.placement]))
  );
  const filterStateKey = useMemo(
    () =>
      JSON.stringify(
        filters.map((filter) => ({
          id: filter.id,
          type: filter.type,
          placement: filter.placement,
          defaultVisible: filter.defaultVisible,
          searchTerm: filter.type === 'search' ? filter.searchTerm ?? null : null,
          selected: filter.type === 'date' ? filter.selected ?? null : null,
          startDate: filter.type === 'dateRange' ? filter.startDate ?? null : null,
          endDate: filter.type === 'dateRange' ? filter.endDate ?? null : null,
          options:
            filter.type === 'select'
              ? filter.options.map((option) => ({
                  value: option.value,
                  selected: option.selected || false,
                }))
              : [],
        }))
      ),
    [filters]
  );
  const visibleFilterIds = useMemo(
    () => new Set(filters.filter((f) => f.defaultVisible).map((f) => f.id)),
    [filterStateKey, filters]
  );

  useEffect(() => {
    setPlacements((prev) =>
      Object.fromEntries(filters.map((f) => [f.id, prev[f.id] || f.placement]))
    );
    setSelections(
      Object.fromEntries(
        filters
          .filter((f): f is SelectFilterConfig => f.type === 'select')
          .map((f) => [f.id, new Set(f.options.filter((o) => o.selected).map((o) => o.value))])
      )
    );
    setDateSelections(
      Object.fromEntries(
        filters
          .filter((f): f is DateFilterConfig => f.type === 'date')
          .map((f) => [f.id, f.selected ?? null])
      )
    );
    setDateRangeSelections(
      Object.fromEntries(
        filters
          .filter((f): f is DateRangeFilterConfig => f.type === 'dateRange')
          .map((f) => [f.id, [f.startDate ?? null, f.endDate ?? null]])
      )
    );
    setSearchTerms((prev) =>
      Object.fromEntries(
        filters
          .filter((f): f is SearchFilterConfig => f.type === 'search')
          .map((f) => [f.id, f.searchTerm ?? prev[f.id] ?? null])
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStateKey]);

  const togglePlacement = (id: string) => {
    setPlacements((prev) => ({
      ...prev,
      [id]: prev[id] === FilterPlacement.Chiclet ? FilterPlacement.Menu : FilterPlacement.Chiclet,
    }));
  };

  const chicletCount = isMobile
    ? 0
    : filters.filter((f) => (placements[f.id] || f.placement) === FilterPlacement.Chiclet).length;
  const atChicletMax = maxChicletFilters !== undefined && chicletCount >= maxChicletFilters;

  const debouncedSearchOnChange = useMemo(
    () =>
      Object.fromEntries(
        filters
          .filter((f): f is SearchFilterConfig => f.type === 'search')
          .map((f) => [
            f.id,
            f.debounceMs === 0
              ? (term: string | null) => f.onChange(term)
              : debounce((term: string | null) => f.onChange(term), f.debounceMs ?? 1000),
          ])
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const renderFilter = (config: FilterConfig, mode: FilterRenderMode) => {
    if (!visibleFilterIds.has(config.id)) return null;

    switch (config.type) {
      case 'select': {
        const selected = selections[config.id] ?? new Set<string>();
        const selectedFilterCount = Array.from(selected).filter(
          (value) => value.toLowerCase() !== 'all'
        ).length;

        const handleToggle = (_: any, index: number) => {
          const option = config.options[index];
          let next: Set<string>;
          if (config.multiSelect === false) {
            next = selected.has(option.value) ? new Set() : new Set([option.value]);
          } else {
            next = new Set(selected);
            if (next.has(option.value)) next.delete(option.value);
            else next.add(option.value);
          }
          setSelections((prev) => ({ ...prev, [config.id]: next }));
          config.onChange(Array.from(next));
        };

        const handleClear = () => {
          setSelections((prev) => ({ ...prev, [config.id]: new Set() }));
          config.onChange([]);
        };

        return (
          <ChicletFilter
            key={config.id}
            name={config.name}
            icon={config.icon}
            filterCount={selectedFilterCount}
            onFilterClear={handleClear}
            onTogglePlacement={
              isMobile || (mode === FilterRenderMode.Inline && atChicletMax)
                ? undefined
                : () => togglePlacement(config.id)
            }
            renderMode={mode}
            popoverHeight={360}
          >
            <SelectFilter
              name={config.id}
              options={config.options.map((o) => ({ ...o, selected: selected.has(o.value) }))}
              onToggleOption={handleToggle}
            />
          </ChicletFilter>
        );
      }
      case 'date': {
        const selected = dateSelections[config.id] ?? null;

        const handleChange = (date: Date | null) => {
          setDateSelections((prev) => ({ ...prev, [config.id]: date }));
          config.onChange(date);
        };

        const handleClear = () => {
          setDateSelections((prev) => ({ ...prev, [config.id]: null }));
          config.onChange(null);
        };

        return (
          <ChicletFilter
            key={config.id}
            name={config.name}
            icon={config.icon}
            filterCount={selected ? 1 : 0}
            onFilterClear={handleClear}
            onTogglePlacement={
              isMobile || (mode === FilterRenderMode.Inline && atChicletMax)
                ? undefined
                : () => togglePlacement(config.id)
            }
            renderMode={mode}
          >
            <DateFilter selected={selected} onChange={handleChange} native />
          </ChicletFilter>
        );
      }
      case 'dateRange': {
        const [startDate, endDate] = dateRangeSelections[config.id] ?? [null, null];
        const filterCount = [startDate, endDate].filter(Boolean).length;

        const handleChange = (range: [Date | null, Date | null]) => {
          setDateRangeSelections((prev) => ({ ...prev, [config.id]: range }));
          config.onChange(range);
        };

        const handleClear = () => {
          const cleared: [Date | null, Date | null] = [null, null];
          setDateRangeSelections((prev) => ({ ...prev, [config.id]: cleared }));
          config.onChange(cleared);
        };

        return (
          <ChicletFilter
            key={config.id}
            name={config.name}
            icon={config.icon}
            filterCount={filterCount}
            onFilterClear={handleClear}
            onTogglePlacement={
              isMobile || (mode === FilterRenderMode.Inline && atChicletMax)
                ? undefined
                : () => togglePlacement(config.id)
            }
            renderMode={mode}
          >
            <DateFilter
              selectsRange
              startDate={startDate}
              endDate={endDate}
              onChange={handleChange}
              native={config.native}
            />
          </ChicletFilter>
        );
      }
      case 'search': {
        const searchTerm = searchTerms[config.id] ?? undefined;

        const handleChange = (term: string | null) => {
          setSearchTerms((prev) => ({ ...prev, [config.id]: term }));
          debouncedSearchOnChange[config.id]?.(term);
        };

        const handleClear = () => {
          setSearchTerms((prev) => ({ ...prev, [config.id]: null }));
          config.onChange(null);
        };
        return (
          <ChicletFilter
            key={config.id}
            name={config.name}
            icon={config.icon}
            filterCount={searchTerm ? 1 : 0}
            onFilterClear={handleClear}
            onTogglePlacement={
              isMobile || (mode === FilterRenderMode.Inline && atChicletMax)
                ? undefined
                : () => togglePlacement(config.id)
            }
            renderMode={mode}
          >
            <SearchFilter name={config.name} searchTerm={searchTerm} onChange={handleChange} />
          </ChicletFilter>
        );
      }

      case 'custom':
        return config.render(mode);
    }
  };

  const chicletFilters = isMobile
    ? []
    : filters.filter((f) => (placements[f.id] || f.placement) === FilterPlacement.Chiclet);
  const menuFilters = isMobile
    ? filters
    : filters.filter((f) => (placements[f.id] || f.placement) === FilterPlacement.Menu);

  const activeMenuFilterCount = menuFilters.reduce((count, f) => {
    if (!visibleFilterIds.has(f.id)) return count;
    switch (f.type) {
      case 'select':
        return (
          count +
          (Array.from(selections[f.id] ?? []).some((value) => value.toLowerCase() !== 'all')
            ? 1
            : 0)
        );
      case 'date':
        return count + (dateSelections[f.id] ? 1 : 0);
      case 'dateRange': {
        const [s, e] = dateRangeSelections[f.id] ?? [null, null];
        return count + (s || e ? 1 : 0);
      }
      case 'search':
        return count + (searchTerms[f.id] ? 1 : 0);
      default:
        return count;
    }
  }, 0);

  return (
    <div className="page-filters">
      {chicletFilters.map((f) => renderFilter(f, FilterRenderMode.Chiclet))}
      {menuFilters.length > 0 && (
        <FilterMenu
          name={isMobile ? 'Filters' : 'More Filters'}
          icon={<MdFilterList />}
          filterCount={activeMenuFilterCount}
        >
          {menuFilters.map((f) => renderFilter(f, FilterRenderMode.Inline))}
        </FilterMenu>
      )}
    </div>
  );
};
